import { addOrUpdateElement } from '../helpers'
import { Order } from '../order/types'
import { WindowsAction } from './actions'
import {
  AggressorWindowInfo,
  LastLookWindowInfo,
  RfqWindowInfo,
  SecurityModalInfo
} from './types'

export interface State {
  aggressorWindows: AggressorWindowInfo[]
  lastLookWindows: LastLookWindowInfo[]
  rfqWindows: RfqWindowInfo[]
  activeRfqWindow: number | undefined
  aggressorForegroundIndex: number | undefined
  securitiesModalInfo: Record<Order['id'], SecurityModalInfo | undefined>
  windowFront: string
}

export const initialState: State = {
  aggressorWindows: [],
  lastLookWindows: [],
  rfqWindows: [],
  activeRfqWindow: undefined,
  aggressorForegroundIndex: undefined,
  securitiesModalInfo: {},
  windowFront: 'depth'
}

export const securityWindowsMatch = (
  windowOne: SecurityModalInfo,
  windowTwo: SecurityModalInfo
) => windowOne.orderId === windowTwo.orderId

const findAggressorWindowIndex = (
  initialOrderId: Order['id'],
  windows: AggressorWindowInfo[],
  currentIndex: number | undefined
) => {
  const index = windows.findIndex((w) => w.initialOrderId === initialOrderId)
  return index === -1 ? currentIndex : index
}

export default (state = initialState, action: WindowsAction): State => {
  switch (action.type) {
    case 'windows.foregroundAggressorWindow':
      return {
        ...state,
        aggressorForegroundIndex: findAggressorWindowIndex(
          action.payload,
          state.aggressorWindows,
          state.aggressorForegroundIndex
        )
      }
    case 'windows.foregroundAggressorWindowAtIndex':
      return {
        ...state,
        aggressorForegroundIndex: action.payload
      }
    case 'windows.updateDataForSecurityModal':
      return {
        ...state,
        securitiesModalInfo: {
          ...state.securitiesModalInfo,
          [action.payload.initialOrderId]: action.payload.securityModalInfo
        }
      }
    case 'windows.addAggressorWindow':
      return {
        ...state,
        aggressorWindows: [action.payload, ...state.aggressorWindows]
      }
    case 'windows.addLastLookWindow':
      return {
        ...state,
        lastLookWindows: addOrUpdateElement(
          state.lastLookWindows,
          action.payload,
          (w1, w2) => w1.orderId === w2.orderId
        )
      }
    case 'windows.addRfqWindow': {
      const rfqWindows = [
        { ...action.payload, isOpen: true },
        ...state.rfqWindows.filter((w) => w.orderId !== action.payload.orderId)
      ]
      return {
        ...state,
        rfqWindows,
        activeRfqWindow: 0
      }
    }
    case 'windows.removeAggressorWindow':
      return {
        ...state,
        aggressorWindows: state.aggressorWindows.filter(
          (window) => window !== action.payload
        )
      }
    case 'windows.removeLastLookFromWindows':
      return {
        ...state,
        lastLookWindows: state.lastLookWindows.filter(
          (window) => window.orderId !== action.payload.window.orderId
        )
      }
    case 'windows.removeRfqWindow': {
      const rfqWindows = state.rfqWindows.filter(
        (w) => w.orderId !== action.payload.orderId
      )
      return {
        ...state,
        rfqWindows,
        activeRfqWindow: rfqWindows.length ? 0 : undefined
      }
    }
    case 'windows.hideRfqWindow': {
      const rfqWindows = state.rfqWindows.map((w) =>
        w.orderId === action.payload.orderId ? { ...w, isOpen: false } : w
      )
      const activeRfqWindow = rfqWindows.findIndex((w) => w.isOpen)
      return {
        ...state,
        rfqWindows,
        activeRfqWindow
      }
    }
    case 'windows.showRfqWindow': {
      const rfqWindows = state.rfqWindows.map((w) =>
        w.orderId === action.payload.orderId ? { ...w, isOpen: true } : w
      )
      const activeRfqWindow = rfqWindows.findIndex((w) => w.isOpen)
      return {
        ...state,
        rfqWindows,
        activeRfqWindow
      }
    }
    case 'windows.setRfqActiveWindowIndex': {
      return {
        ...state,
        activeRfqWindow: action.payload
      }
    }
    case 'windows.setWindowFront':
      return {
        ...state,
        windowFront: action.payload
      }
    case 'windows.openAggressorWindow':
    default:
      return state
  }
}
